import React, { useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';

import DialogTitle from '../shared/DialogTitle';
import Loading from '../shared/Loading';
import ServerError from '../error/ServerError';
import { START_TRAINING_PLAN_SESSION } from '../../graphql/training_plan';
import { GET_USERS } from '../../graphql/users';

const USER_LIMIT = 100;

export default function AssignTrainingPlanDialog({
  open,
  onClose,
  trainingPlan,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);

  const { loading, error, data } = useQuery(GET_USERS, {
    skip: !open,
    variables: {
      input: { trainerId: trainingPlan?.userId || null, limit: USER_LIMIT },
      trainerId: trainingPlan?.userId || null,
    },
  });

  const [startTrainingPlanSession, { loading: saving }] = useMutation(
    START_TRAINING_PLAN_SESSION,
    {
      onCompleted: (data) => {
        enqueueSnackbar(
          'Training plan assigned. Member notification will be sent.',
          {
            variant: 'success',
          }
        );
        setSelectedUserId(null);
        setSearchTerm('');
        onClose?.(data?.startTrainingPlanSession);
      },
      onError: (mutationError) => {
        enqueueSnackbar(`Error: ${mutationError.message}`, {
          variant: 'error',
        });
      },
    }
  );

  const users = data?.users || [];
  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      return users;
    }

    return users.filter((user) =>
      [user.name, user.email].some((value) =>
        (value || '').toLowerCase().includes(term)
      )
    );
  }, [searchTerm, users]);

  const selectedUser = users.find((user) => user._id === selectedUserId);

  const handleAssign = async () => {
    if (!selectedUserId) {
      enqueueSnackbar('Select an athlete first.', { variant: 'warning' });
      return;
    }

    await startTrainingPlanSession({
      variables: {
        trainingPlanId: trainingPlan._id,
        userId: selectedUserId,
      },
    });
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle onClose={onClose}>Assign to Athlete</DialogTitle>
      <DialogContent dividers>
        <Box mb={2}>
          <Typography sx={{ color: '#40536b', fontSize: 14, mb: 1 }}>
            Choose an athlete to enroll in {trainingPlan?.title || 'this plan'}.
          </Typography>
          <TextField
            fullWidth
            autoFocus
            placeholder="Search athletes..."
            size="small"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </Box>

        {error && (
          <ServerError message={error.message || 'Failed to load athletes'} />
        )}
        {loading && <Loading />}
        {!loading && !error && filteredUsers.length === 0 && (
          <Typography color="textSecondary">No athletes found.</Typography>
        )}
        {!loading && !error && filteredUsers.length > 0 && (
          <List
            disablePadding
            sx={{
              border: '1px solid #dbe3ee',
              borderRadius: 1,
              maxHeight: 360,
              overflowY: 'auto',
            }}
          >
            {filteredUsers.map((user) => (
              <ListItemButton
                key={user._id}
                selected={user._id === selectedUserId}
                onClick={() => setSelectedUserId(user._id)}
              >
                <ListItemAvatar>
                  <Avatar src={user.avatarUrl}>
                    {(user.name || user.email || '?').charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={user.name || user.email || 'Unnamed athlete'}
                  secondary={user.email}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={saving || !selectedUser}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}
